import { Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import {
  UserAddOutlined,
  TeamOutlined,
  FundOutlined,
  DollarOutlined,
  TrophyOutlined,
  ScheduleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import {
  compactAmount,
  fullAmount,
  pctChange,
  previousLabel,
} from "../crmDashboardUtils";

// change === null means there is no previous-period figure to compare with.
const Trend = ({ change, hint }) => {
  if (change === null) {
    return (
      <Tooltip title="Nothing to compare with in the previous period">
        <span className="crm-trend neutral">New</span>
      </Tooltip>
    );
  }
  const up = change >= 0;
  const flat = Math.abs(change) < 0.05;
  return (
    <Tooltip title={hint}>
      <span className={`crm-trend ${flat ? "neutral" : up ? "up" : "down"}`}>
        {!flat && (up ? <ArrowUpOutlined /> : <ArrowDownOutlined />)}
        {flat ? "0%" : `${up ? "+" : ""}${change.toFixed(0)}%`}
      </span>
    </Tooltip>
  );
};

const DashboardSummary = ({ data = {}, currency, country, period }) => {
  const navigate = useNavigate();
  const vs = `vs ${previousLabel(period)}`;

  const overdue = Number(data.overdue_tasks || 0);

  const cards = [
    {
      key: "new",
      title: "New leads",
      icon: <UserAddOutlined />,
      accent: "#2a78d6",
      value: Number(data.new_in_range || 0).toLocaleString(),
      trend: <Trend change={pctChange(data.new_in_range, data.new_prev)} hint={`${vs} (${data.new_prev || 0})`} />,
      sub: `${data.new_today || 0} today`,
      to: "/crm/leads",
    },
    {
      key: "total",
      title: "Total leads",
      icon: <TeamOutlined />,
      accent: "#4a3aa7",
      value: Number(data.total_leads || 0).toLocaleString(),
      sub: "All leads on record",
      to: "/crm/leads",
    },
    {
      key: "pipeline",
      title: "Open pipeline",
      icon: <FundOutlined />,
      accent: "#eb6834",
      value: compactAmount(data.pipeline_value, country),
      unit: currency,
      title_hint: `${fullAmount(data.pipeline_value)} ${currency}`,
      sub: `${data.open_deals || 0} open deal${Number(data.open_deals) === 1 ? "" : "s"}`,
      to: "/crm/deals",
    },
    {
      key: "revenue",
      title: "Won revenue",
      icon: <DollarOutlined />,
      accent: "#1baf7a",
      value: compactAmount(data.revenue_in_range, country),
      unit: currency,
      title_hint: `${fullAmount(data.revenue_in_range)} ${currency}`,
      trend: <Trend change={pctChange(data.revenue_in_range, data.revenue_prev)} hint={`${vs} (${fullAmount(data.revenue_prev)} ${currency})`} />,
      sub: `${data.won_in_range || 0} deal${Number(data.won_in_range) === 1 ? "" : "s"} won`,
      to: "/crm/deals",
    },
    {
      key: "win",
      title: "Win rate",
      icon: <TrophyOutlined />,
      accent: "#eda100",
      value: data.win_rate === null || data.win_rate === undefined ? "—" : `${data.win_rate}%`,
      sub:
        data.win_rate === null || data.win_rate === undefined
          ? "No closed deals yet"
          : `${data.won_deals || 0} won all time · avg ${compactAmount(data.avg_deal_size, country)}`,
      to: "/crm/deals",
    },
    {
      key: "tasks",
      title: "Overdue tasks",
      icon: <ScheduleOutlined />,
      accent: overdue > 0 ? "#d03b3b" : "#0ca30c",
      value: overdue.toLocaleString(),
      danger: overdue > 0,
      sub: `${data.tasks_today || 0} due today · ${data.upcoming_tasks || 0} upcoming`,
      to: "/crm/tasks",
    },
  ];

  return (
    <div className="crm-kpi-grid">
      {cards.map((c) => (
        <button
          type="button"
          key={c.key}
          className="crm-kpi"
          style={{ "--kpi-accent": c.accent }}
          onClick={() => navigate(c.to)}
        >
          <span className="crm-kpi-top">
            <span className="crm-kpi-title">{c.title}</span>
            <span className="crm-kpi-icon">{c.icon}</span>
          </span>
          <span className="crm-kpi-valuerow">
            <span
              className={`crm-kpi-value${c.danger ? " danger" : ""}`}
              title={c.title_hint}
            >
              {c.value}
            </span>
            {c.unit && <span className="crm-kpi-unit">{c.unit}</span>}
          </span>
          <span className="crm-kpi-foot">
            {c.trend}
            <span className="crm-kpi-sub">{c.sub}</span>
          </span>
        </button>
      ))}
    </div>
  );
};

export default DashboardSummary;
